import { PRODUCT_TYPE } from "@/constants";

import { USD_STABLECOINS_REGEX } from "./formatCurrency";

type ResourceType = "currencies" | "digital-assets" | string;

/**
 * Item with basic product info for sorting
 */
export interface SortableProduct {
  id: string;
  resourceType?: ResourceType;
  isTradable?: boolean;
}

/**
 * Options for sorting products
 */
export interface SortProductsOptions {
  /** Base currency to prioritize first */
  baseCurrency?: string;
  /** Whether to sort fiat before digital assets (default: true) */
  fiatFirst?: boolean;
  /** Whether to prioritize USD stablecoins within digital assets (default: true) */
  usdStablecoinsFirst?: boolean;
  /** Whether to sort tradable products before non-tradable (default: false) */
  tradableFirst?: boolean;
}

/**
 * Returns a numeric category rank for a product based on its asset type:
 * 0 = fiat, 1 = stablecoin (digital-asset matching USD regex), 2 = crypto (other digital-asset)
 */
function getAssetCategoryRank(product: SortableProduct): number {
  if (product.resourceType === "currencies") return 0;
  if (
    product.resourceType === "digital-assets" &&
    USD_STABLECOINS_REGEX.test(product.id)
  )
    return 1;
  return 2;
}

/**
 * Creates a comparison function for sorting products.
 *
 * Default sort order:
 * 1. Base currency first (if specified)
 * 2. Fiat currencies before digital assets
 * 3. USD stablecoins (USDC, USDT, etc.) before other digital assets
 * 4. Fiat majors (FIAT_MAJORS order), then other fiat alphabetically
 *
 * With `tradableFirst: true`:
 * 1. Fiat tradable → Stablecoins tradable → Crypto tradable
 * 2. Fiat not tradable → Stablecoins not tradable → Crypto not tradable
 * 3. Fiat majors, then other fiat alphabetically, within each group
 */
export function createProductComparator<T extends SortableProduct>(
  options: SortProductsOptions = {}
): (a: T, b: T) => number {
  const {
    baseCurrency,
    fiatFirst = true,
    usdStablecoinsFirst = true,
    tradableFirst = false,
  } = options;

  return (a: T, b: T): number => {
    // Base currency first
    if (baseCurrency) {
      if (a.id === baseCurrency) return -1;
      if (b.id === baseCurrency) return 1;
    }

    if (tradableFirst) {
      // Tradable before non-tradable
      const aTradable = a.isTradable ? 0 : 1;
      const bTradable = b.isTradable ? 0 : 1;
      if (aTradable !== bTradable) return aTradable - bTradable;

      // Within each tradability group: fiat → stablecoins → crypto
      const aCat = getAssetCategoryRank(a);
      const bCat = getAssetCategoryRank(b);
      if (aCat !== bCat) return aCat - bCat;

      return compareCurrencyRank(
        { code: a.id, type: a.resourceType },
        { code: b.id, type: b.resourceType }
      );
    }

    // Fiat (currencies) before digital assets
    if (fiatFirst && a.resourceType !== b.resourceType) {
      if (a.resourceType === "currencies") return -1;
      if (b.resourceType === "currencies") return 1;
    }

    // USD stablecoins first within digital assets
    if (
      usdStablecoinsFirst &&
      a.resourceType === "digital-assets" &&
      b.resourceType === "digital-assets"
    ) {
      const aIsUsd = USD_STABLECOINS_REGEX.test(a.id);
      const bIsUsd = USD_STABLECOINS_REGEX.test(b.id);
      if (aIsUsd && !bIsUsd) return -1;
      if (!aIsUsd && bIsUsd) return 1;
    }

    // Fiat majors, then other fiat; stablecoins, then other crypto
    return compareCurrencyRank(
      { code: a.id, type: a.resourceType },
      { code: b.id, type: b.resourceType }
    );
  };
}

/**
 * Sorts an array of products using the standard sorting logic.
 *
 * @param products - Array of products to sort
 * @param options - Sorting options
 * @returns New sorted array (does not mutate original)
 */
export function sortProducts<T extends SortableProduct>(
  products: T[],
  options: SortProductsOptions = {}
): T[] {
  return [...products].sort(createProductComparator(options));
}

/**
 * Checks if a product ID is a USD stablecoin (contains "USD")
 */
export function isUsdStablecoin(productId: string): boolean {
  return USD_STABLECOINS_REGEX.test(productId);
}

/**
 * Fiat majors, in the order they appear ahead of all other fiat.
 *
 * Applied as a TIE-BREAK only: a currency holding value always outranks a
 * zero-balance major, and the base currency is pinned first by each caller.
 */
export const FIAT_MAJORS = ["GBP", "EUR", "USD", "AED", "CHF", "JPY"] as const;

const FIAT_MAJOR_RANK: ReadonlyMap<string, number> = new Map(
  FIAT_MAJORS.map((code, index) => [code, index])
);

/** Account shape accepted by {@link compareCurrencyRank}. */
export interface RankableAccount {
  code: string;
  /** Anything other than `"digital-assets"` — including absent — ranks as fiat. */
  type?: string;
}

/**
 * Tie-break ordering for accounts of equal value:
 *   - fiat before blockchain
 *   - within fiat: FIAT_MAJORS in order, then the rest alphabetically
 *   - within blockchain: USD stablecoins, then the rest alphabetically (unchanged)
 *
 * Deliberately takes NO base currency. Every caller applies its own base-first
 * check before reaching this comparator, and two of them pin an
 * `overrideBaseCurrency` prop rather than the customer's own base currency —
 * folding base handling in here would silently change which base they mean.
 */
export function compareCurrencyRank(
  a: RankableAccount,
  b: RankableAccount
): number {
  const aIsFiat = a.type !== PRODUCT_TYPE.DIGITAL_ASSETS;
  const bIsFiat = b.type !== PRODUCT_TYPE.DIGITAL_ASSETS;

  if (aIsFiat !== bIsFiat) return aIsFiat ? -1 : 1;

  if (aIsFiat) {
    const aMajor = FIAT_MAJOR_RANK.get(a.code);
    const bMajor = FIAT_MAJOR_RANK.get(b.code);
    if (aMajor !== undefined && bMajor !== undefined) return aMajor - bMajor;
    if (aMajor !== undefined) return -1;
    if (bMajor !== undefined) return 1;
    return a.code.localeCompare(b.code);
  }

  const aIsUsd = isUsdStablecoin(a.code);
  const bIsUsd = isUsdStablecoin(b.code);
  if (aIsUsd !== bIsUsd) return aIsUsd ? -1 : 1;
  return a.code.localeCompare(b.code);
}
